// Host geometry in logical pixels. Discovery belongs to the host; visibility and routing
// stay here. Ledge handles keep their identity and look up the latest geometry by id.
import { env } from './core.js';

let provider = null, current = { surfaces: [], occluders: [] };
let geometry = new Map();
const handles = new Map();
export function initWorld(world) {
  provider = world; handles.clear();
  current = { surfaces: [], occluders: [] }; geometry.clear();
  refreshWorld();
}
export function refreshWorld() {
  if (!provider) return;
  current = provider.snapshot();
  geometry = new Map((current.surfaces || []).map(s => [s.id, { weight: 1, ...s, s: s.s ?? s.scale }]));
}
export const home = () => current.home || null;
export const writing = () => provider?.writing || null;
export const floorY = () => current.floor?.y ?? env.H - 6;
export const castleScale = () => home()?.scale ?? Math.min(env.S, 6);
export const castleLeft = () => home()?.x ?? env.W;
export const castleDoorX = () => home()?.doorX ?? env.W / 2;
export const viewport = () => current.viewport || { width: env.W, height: env.H };

// How much companions like the floor next to the other surfaces' weights. A world can supply its
// own (floor.weight): on a web page the floor lies over the text, so they'd rather be elsewhere.
export const FLOOR_WEIGHT = 1.2;
export const FLOOR = { id: 'floor', ownerId: null, kind: 'floor',
  get weight() { return current.floor?.weight ?? FLOOR_WEIGHT; },
  geom: () => ({ x1: current.floor?.x1 ?? 0, x2: current.floor?.x2 ?? castleLeft(), y: floorY() }) };
// Where on the floor companions may stay (stand around, sit, nap, pull out a bed or throw a party).
// A world whose floor lies over content (a web page) gives `floor.rest`: the stretches [x1, x2] with
// nothing under them. Elsewhere they only pass by. Without it, the whole floor.
export const floorRest = () => current.floor?.rest ?? null;
export function restsOnFloor(x1, x2 = x1) {
  const rest = floorRest();
  return !rest || rest.some(([a, b]) => x1 >= a && x2 <= b);
}
// The parts of [lo, hi] where they may stay that are at least `w` wide: all of it without `rest`.
export function floorStretches(lo, hi, w = 0) {
  const rest = floorRest(), pieces = rest ? rest.map(([a, b]) => [Math.max(lo, a), Math.min(hi, b)]) : [[lo, hi]];
  return pieces.filter(([a, b]) => b - a >= w);
}

export function discoverLedges() {
  refreshWorld();
  const list = [];
  for (const surface of current.surfaces || []) {
    if (surface.id === 'floor' || surface.riding) continue;  // occupied off-screen spots aren't destinations
    let l = handles.get(surface.id);
    if (!l) { l = { geom: () => geometry.get(surface.id) || null }; handles.set(surface.id, l); }
    Object.assign(l, geometry.get(surface.id));
    list.push(l);
  }
  list.push(FLOOR);
  return list;
}
export const windowList = () => current.occluders || [];
const zOf = ownerId => windowList().find(w => w.id === ownerId)?.z ?? -Infinity;
// The floor is behind every window unless the host says what it lies on (a desktop taskbar).
const zOfLedge = l => l === FLOOR ? (current.floor?.z ?? -Infinity) : zOf(l.ownerId);
function coveredAt(x, y, z, wins) {
  if (x < 0 || x > env.W || y < 0 || y > env.H) return true;
  return wins.some(w => w.z > z && x >= w.l && x < w.r && y >= w.t && y < w.b);
}
export function visibleOn(ledge, x, y, s, wins = windowList()) {
  const z = zOfLedge(ledge), half = 8 * s, h = 7 * s;
  const pts = [[x, y + 1], [x - half, y - 2], [x + half, y - 2], [x, y - h], [x - half * 0.6, y - h * 0.7], [x + half * 0.6, y - h * 0.7]];
  return pts.every(([px, py]) => !coveredAt(px, py, z, wins));
}
// Whether the point (x, y) of a surface, or of the window it belongs to, shows: on screen, with no
// window in front of it (a climber's wall: abilities/climb.js).
export const showsAt = (ledge, x, y, wins = windowList()) => !coveredAt(x, y, zOfLedge(ledge), wins);

// ---------- walls ----------
// A wall is where a surface meets something taller: the screen edge or the sandcastle on the
// floor, a higher window rising above a lower window's edge, or the rounded inner ends of the
// search bar. The crab leans its shoulder on it.
export function discoverWalls(ledges = discoverLedges(), wins = windowList()) {
  const walls = [];
  const minH = 9 * env.S;
  // The floor's own ends (a host can narrow the floor: a page keeps a corner free for its button).
  walls.push({ id: 'floor:edgeL', base: FLOOR, wallSide: 'L', geom: () => { const g = FLOOR.geom(); return { ...g, wallX: g.x1 }; } });
  walls.push({ id: 'floor:castle', base: FLOOR, wallSide: 'R', geom: () => { const g = FLOOR.geom(); return { ...g, wallX: g.x2 }; } });
  for (const l of ledges) {
    if (l.kind === 'pill') {
      // It gets there over the bar's top, then hops down inside.
      const top = ledges.find(x => x.id === l.id.replace(/:in[LR]$/, ''));
      walls.push({ id: l.id, base: top || l, wallSide: l.id.endsWith('R') ? 'R' : 'L', pill: true, inside: l, geom: () => l.geom() });
      continue;
    }
    if (l.kind !== 'edge' && l.kind !== 'floor') continue;
    const z = zOfLedge(l);
    for (const w of wins) {
      if (w.z <= z) continue;
      for (const side of ['R', 'L']) {
        const id = `${l.id}|${w.id}:${side}`;
        const geom = () => {
          const g = l.geom(), b = windowList().find(x => x.id === w.id);
          if (!g || !b || b.z <= z) return null;
          if (!(b.t < g.y - minH && b.b > g.y)) return null;      // it must rise above the surface
          const room = 12 * env.S;
          // A wall on the crab's right is the higher window's left side, and the other way round.
          if (side === 'R') return b.l > g.x1 + room && b.l <= g.x2 ? { x1: g.x1, x2: b.l, y: g.y, wallX: b.l } : null;
          return b.r < g.x2 - room && b.r >= g.x1 ? { x1: b.r, x2: g.x2, y: g.y, wallX: b.r } : null;
        };
        if (geom()) walls.push({ id, base: l, wallSide: side, geom });
      }
    }
  }
  return walls;
}

// Where the rounded end of a bar is at height y.
export function pillWallX(pill, side, y) {
  const R = (pill.b - pill.t) / 2, cy = pill.t + R;
  const dx = Math.sqrt(Math.max(0, R * R - (y - cy) * (y - cy)));
  return side === 'R' ? pill.r - R + dx : pill.l + R - dx;
}

// ---------- undersides: where a companion can hang by its feet (rest 'hang': a bat roosting) ----------
// What the host offers (`undersides` in its snapshot: a window's bottom edge, a title bar's
// bottom, the underside of a line of text), each { id, ownerId, x1, x2, y, room, scale }: room is
// the free height below it (see fits() in companion.js), scale as on a surface. Like ledges, the
// handles keep their identity and look up the latest geometry (null once it's gone).
const unders = new Map();
export function discoverUndersides() {
  refreshWorld();
  return (current.undersides || []).map(({ id, ownerId }) => {
    if (!unders.has(id)) unders.set(id, { id, ownerId, kind: 'under', weight: 1, geom: () => {
      const u = (current.undersides || []).find(q => q.id === id);
      return u ? { ...u, s: u.s ?? u.scale } : null;
    } });
    return unders.get(id);
  });
}
// What hangs at (x, y) under it shows: the point it hangs from, and its body below, aren't behind
// a window in front of what it hangs from (visibleOn, upside down).
export function visibleUnder(under, x, y, s, wins = windowList()) {
  const z = zOf(under.ownerId), half = 8 * s, h = 7 * s;
  const pts = [[x, y - 1], [x - half, y + 2], [x + half, y + 2], [x, y + h], [x - half * 0.6, y + h * 0.7], [x + half * 0.6, y + h * 0.7]];
  return pts.every(([px, py]) => !coveredAt(px, py, z, wins));
}

// ---------- falling: the highest visible surface below a point ----------
export function surfaceBelow(x, y, ledges = discoverLedges(), wins = windowList(), ok = null) {
  let best = null;
  for (const l of ledges) {
    if (l.kind === 'pill' || l.kind === 'text') continue;   // falls land on solid things
    if (ok && l !== FLOOR && !ok(l)) continue;               // ...it fits on (see fits() in companion.js)
    const g = l.geom();
    if (!g || x < g.x1 || x > g.x2 || g.y < y - 2) continue;
    if (l !== FLOOR && coveredAt(x, g.y + 1, zOf(l.ownerId), wins)) continue;
    if (!best || g.y < best.g.y) best = { ledge: l, g };
  }
  return best || { ledge: FLOOR, g: FLOOR.geom() };
}

// ---------- routes made of small steps ----------
// A route is a list of hops between ledges, each short enough for one small jump. Long
// trips with no such route use the fancier ways to travel instead.
export function hopLimits(s) { return { gap: 14 * s, up: 13 * s, down: 34 * s }; }

function hopBetween(A, ga, B, gb, s) {
  const lim = hopLimits(s);
  const dy = gb.y - ga.y;
  if (dy < -lim.up || dy > lim.down) return null;
  // Keep clear of the ends, except on a single point (the caret, the deck chair's seat).
  const ma = ga.x2 - ga.x1 > 16 * s ? 7 * s : 0, mb = gb.x2 - gb.x1 > 16 * s ? 7 * s : 0;
  const aLo = ga.x1 + ma, aHi = ga.x2 - ma, bLo = gb.x1 + mb, bHi = gb.x2 - mb;
  if (aHi < aLo || bHi < bLo) return null;
  const lo = Math.max(aLo, bLo), hi = Math.min(aHi, bHi);
  if (lo <= hi) {                               // one above the other: hop straight up or down
    if (Math.abs(dy) < 2 * s) return null;      // the same height and overlapping: not a hop
    const x = (lo + hi) / 2;
    return { from: A, to: B, takeoff: x, land: x };
  }
  if (bLo > aHi) {                              // B is to the right
    if (bLo - aHi > lim.gap) return null;
    return { from: A, to: B, takeoff: aHi, land: bLo };
  }
  if (aLo - bHi > lim.gap) return null;         // B is to the left
  return { from: A, to: B, takeoff: aLo, land: bHi };
}

export function planRoute(fromLedge, toLedge, s, maxHops = 5) {
  if (fromLedge.id === toLedge.id) return [];
  const ledges = discoverLedges().filter(l => l.kind !== 'pill');
  for (const extra of [fromLedge, toLedge]) if (!ledges.some(l => l.id === extra.id)) ledges.push(extra);   // the caret, the deck chair
  const geoms = new Map(ledges.map(l => [l.id, l.geom()]));
  const byId = new Map(ledges.map(l => [l.id, l]));
  const start = byId.get(fromLedge.id), goal = byId.get(toLedge.id);
  if (!start || !goal || !geoms.get(start.id) || !geoms.get(goal.id)) return null;
  const prev = new Map([[start.id, null]]);
  let frontier = [start];
  for (let depth = 0; depth < maxHops && frontier.length; depth++) {
    const next = [];
    for (const A of frontier) {
      const ga = geoms.get(A.id);
      for (const B of ledges) {
        if (prev.has(B.id)) continue;
        const gb = geoms.get(B.id);
        if (!gb) continue;
        if ((A.kind === 'text' || B.kind === 'text') && A.ownerId !== B.ownerId) continue;   // text is reached from inside its own window
        const hop = hopBetween(A, ga, B, gb, s);
        if (!hop) continue;
        // Never through a window: a hop that takes off or lands where a window in front hides
        // the surface (straight down from a window's top onto the one behind it) isn't a route.
        if (!visibleOn(A, hop.takeoff, ga.y, s) || !visibleOn(B, hop.land, gb.y, s)) continue;
        prev.set(B.id, hop);
        if (B.id === goal.id) {
          const route = [];
          for (let h = hop; h; h = prev.get(h.from.id)) route.unshift(h);
          return route;
        }
        next.push(B);
      }
    }
    frontier = next;
  }
  return null;
}
